import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import DialogContentText from '@mui/material/DialogContentText'
import ListItem from '@mui/material/ListItem'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMutation } from '@tanstack/react-query'
import { KNBulletList } from 'components/KNBulletList/KNBulletList'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormText from 'components/KNForm/KNFormText'
import { analyticsEvent } from 'global/helpers/analytics'
import { processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import { regexEmail } from 'global/helpers/validators'
import i18n from 'i18n'
import { type ReactElement, useEffect, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { requestAccountInWeblink } from 'screens/Weblink/Weblink.service'
import type { RequestAccountFormValues } from 'screens/Weblink/Weblink.types'

interface RequestAccountDialogPayload {
  weblinkToken: string
  promo?: boolean
}

export interface RequestAccountDialogProps {
  payload: RequestAccountDialogPayload
  open: boolean
  onAction: () => void
  onClose: () => void
}

const RequestAccountDialog = ({ payload, open, onAction, onClose }: RequestAccountDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, formState, setError } = useForm<RequestAccountFormValues>()
  const [showConfirmationSnackbar, setShowConfirmationSnackbar] = useState(false)
  const requestAccountMutation = useMutation({
    mutationFn: (data: RequestAccountFormValues) => requestAccountInWeblink(data, payload.weblinkToken),
    onError: (error) => setError('root', processServerErrorMessages(error)),
  })

  useEffect(() => {
    if (open) {
      reset(
        processDefaultValues({
          language: i18n.language,
        }),
      )
    }
  }, [open])

  const onSubmit: SubmitHandler<RequestAccountFormValues> = async (data: RequestAccountFormValues) => {
    await requestAccountMutation.mutateAsync(data)
    onAction()
    setShowConfirmationSnackbar(true)
    analyticsEvent('polestar_cs_account_request_sent')
  }

  return (
    <>
      <KNDialog
        open={open}
        onClose={onClose}
        closeLabel={payload.promo ? t('general.skip') : t('general.cancel')}
        preventClosing={formState.isSubmitting}
        title={t('screens.cs.weblink.request_account.request_account')}
        actions={
          <Button
            color="secondary"
            variant="contained"
            loading={formState.isSubmitting}
            onClick={handleSubmit(onSubmit)}
          >
            {t('screens.cs.weblink.request_account.send_request')}
          </Button>
        }
        onSubmit={handleSubmit(onSubmit)}
      >
        <KNDialogFormErrors errors={formState.errors?.root} />
        {payload.promo && (
          <DialogContentText component="div" mb={3}>
            <Typography color="text.main">{t('screens.cs.weblink.request_account_promo.intro')}</Typography>
            <KNBulletList sx={{ color: 'text.main' }}>
              <ListItem>{t('screens.cs.weblink.request_account_promo.bullet_1')}</ListItem>
              <ListItem>{t('screens.cs.weblink.request_account_promo.bullet_2')}</ListItem>
              <ListItem>{t('screens.cs.weblink.request_account_promo.bullet_3')}</ListItem>
            </KNBulletList>
            <Typography color="text.main">{t('screens.cs.weblink.request_account_promo.outro')}</Typography>
          </DialogContentText>
        )}
        <Stack spacing={1}>
          <KNFormText
            name="name"
            label={t('screens.cs.weblink.request_account.name')}
            control={control}
            rules={{
              required: t('form.validation.required'),
            }}
          />
          <KNFormText
            name="email"
            label={t('screens.cs.weblink.request_account.email')}
            control={control}
            rules={{
              pattern: {
                value: regexEmail,
                message: t('form.validation.invalid_email'),
              },
              required: t('form.validation.required'),
            }}
          />
          <KNFormText
            name="companyName"
            label={t('screens.cs.weblink.request_account.company')}
            control={control}
            rules={{
              required: t('form.validation.required'),
            }}
          />
        </Stack>
      </KNDialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={showConfirmationSnackbar}
        onClose={(): void => setShowConfirmationSnackbar(false)}
        key="requestAccountConfirmation"
      >
        <Alert variant="filled" severity="success">
          {t('screens.cs.weblink.request_account.confirmation')}
        </Alert>
      </Snackbar>
    </>
  )
}

export default RequestAccountDialog
